import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'core-js-pure/stable';
import 'core-js-pure/features/string/repeat';
import 'core-js-pure/es/promise';
import 'regenerator-runtime/runtime';
import 'es6-promise';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Route, Switch } from 'react-router-dom';
import Store, { browserHistory } from 'state/Store';
import { ConnectedRouter } from 'connected-react-router';
import {
  MountSpan,
  SpanRegistrarProvider
  // useOtelContext
} from '@trp-ta-nitro/otel-react';
import {
  initializeFaro,
  createReactRouterV4Options,
  ReactIntegration,
  getWebInstrumentations
} from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { isEmpty } from 'lodash';

import Provider from 'components/shared/Provider/Provider';
import ErrorBoundary from 'components/shared/ErrorBoundary/ErrorBoundary';
import ErrorPage from 'components/shared/ErrorPage/ErrorPage';
import ConfigProvider, {
  ConfigContext
} from 'components/config/ConfigProvider';
import App, { SendTo } from './App';
import { AuthGate, AuthServiceProvider } from '@trp-ta-nitro/auth-react';
import PostLogoutPage from 'components/pages/Logout/PostLogoutPage';
import { addBrowserCookie } from 'support/BrowserUtils';
import { addSessionStorageParams } from 'support/SessionStorageUtils';
import { pingServer } from 'services/Rest';
import { GLOBALS } from 'services/globals';

import 'style/main.scss';
import { LicenseManager } from 'ag-grid-enterprise';
import PageLoadingSpinner from 'components/layouts/Navigation/PageLoadingSpinner';
import MarginJournal from 'components/pages/MarginJournal/MarginJournal';
import {
  frontendMonitoringEnabled,
  marginJournalEnabled
} from 'services/FeatureFlags';

import OtelProvider from './services/Observability/OtelProvider';
import {
  getTracer,
  getGlobalSpanRegistrar
} from './services/Observability/otel';
import MarginJournalPage from './components/pages/MarginJournal/MarginJournalPage';
import { LOGIN } from './services/Externals';
import { diag, DiagConsoleLogger, DiagLogLevel } from '@opentelemetry/api';

LicenseManager.setLicenseKey(
  'CompanyName=SHI International Corp_on_behalf_of_T. Rowe Price Associates Group (Owings Mills, MD),LicensedApplication=Account Access Cloud,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=3,LicensedProductionInstancesCount=1,AssetReference=AG-014711,ExpiryDate=2_April_2022_[v2]_MTY0ODg1NDAwMDAwMA==601167b007839db19868dfe1bc3e2691'
);

diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.NONE);

const Ping = () => {
  return pingServer();
};

const AccountAccess = ({ location: { search } }) => {
  // pre-applcation work
  const [initFaro, setInitizedFaro] = useState(false);
  if (search) {
    addSessionStorageParams(search);
    addBrowserCookie('additionalParams', search);
  }
  return (
    <ConfigContext.Consumer>
      {({ config }) => {
        try {
          if (frontendMonitoringEnabled() && !initFaro) {
            initializeFaro({
              url: 'https://faro-collector-prod-us-central-0.grafana.net/collect/c024b5f0888da3e9a650b058609e7197',
              app: {
                name: 'II Account Access',
                version: config?.version,
                environment: config?.environment || 'unknown'
              },

              instrumentations: [
                // Mandatory, omits default instrumentations otherwise.
                ...getWebInstrumentations(),

                // Tracing package to get end-to-end visibility for HTTP requests.
                new TracingInstrumentation(),

                // React integration for React applications.
                new ReactIntegration({
                  router: createReactRouterV4Options({
                    history: browserHistory, // the history object used by react-router
                    Route // Route component imported from react-router package
                  })
                })
              ]
            });
            setInitizedFaro(true);
          }
        } catch (e) {
          // if we can't get monitoring, we are still going to load the app
        }
        if (isEmpty(config)) {
          return <PageLoadingSpinner name="Configuration..." />;
        }

        if (!isEmpty(config)) {
          let options = {
            apiKey: config.apiKey,
            types: {
              external: {},
              internal: {
                customerWebViewEnabled: true
              }
            }
          };

          // eslint-disable-next-line no-undef
          if (process.env.REACT_APP_MOCK === 'true') {
            options.types.external.authenticateUrl = `https://localhost.troweprice.com:8080${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/api/clx-tk/token-exchange/service/outbound2`;
            options.types.external.tokenExchangeUrl = `https://localhost.troweprice.com:8080${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/api/token-exchange-service/service/token`;
            options.types.external.authUrl = `https://localhost.troweprice.com:8080${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/api/v0/ciam-public/me`;
            options.types.external.echoUrl = {
              host: 'localhost.troweprice.com:8080',
              path: '/api/echo'
            };
          }
          return (
            <ErrorBoundary name="auth-provider">
              <AuthServiceProvider options={options}>
                <AuthGate
                  externalEntitlements={[
                    ['TRP_RET_EXTERNAL_USER_ACCESS'],
                    ['TRP_GPS_USER_ACCESS'],
                    ['TRP_RPS_EXTERNAL_USER_ACCESS']
                  ]}
                  internalEntitlements={[
                    ['su-admins'],
                    ['portal_retaildesktop_base']
                  ]}
                  unauthenticated={() => <SendTo target={LOGIN} />}
                  unauthorized={() => <ErrorPage />}
                  loading={() => (
                    <PageLoadingSpinner name="User Settings..." />
                  )}>
                  <ErrorBoundary>
                    <App apiKey={config.apiKey} version={config.version} />
                  </ErrorBoundary>
                </AuthGate>
              </AuthServiceProvider>
            </ErrorBoundary>
          );
        }
      }}
    </ConfigContext.Consumer>
  );
};

ReactDOM.render(
  <Provider store={Store}>
    <OtelProvider>
      <SpanRegistrarProvider registrar={getGlobalSpanRegistrar()}>
        <MountSpan name="appload" tracer={getTracer()}>
          <ConfigProvider>
            <ConnectedRouter history={browserHistory}>
              <Switch>
                <Route exact component={PostLogoutPage} path="/post-logout" />
                <Route exact component={Ping} path="/ping" />
                <Route
                  exact
                  component={
                    marginJournalEnabled() ? MarginJournal : MarginJournalPage
                  }
                  path="/margin-journal"
                />
                <Route component={AccountAccess} />
              </Switch>
            </ConnectedRouter>
          </ConfigProvider>
        </MountSpan>
      </SpanRegistrarProvider>
    </OtelProvider>
  </Provider>,
  document.getElementById('root')
);
